"use client";

import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import {
  Download,
  Scan,
  Map,
  Sparkles,
  LayoutGrid,
  TrendingUp,
} from "lucide-react";

const features = [
  {
    icon: Download,
    badge: "Setup in 48h",
    title: "Importazione Automatica",
    description:
      "Clienti, Mandanti, Ordini, Fatture — importati automaticamente. Se non lo sai fare, lo facciamo noi.",
    color: "bg-blue-50 text-blue-600",
  },
  {
    icon: Scan,
    badge: "Risparmia 5-10h/settimana",
    title: "Documenti → Dati (AI OCR)",
    description:
      "Foto di ordini e fatture → dati inseriti automaticamente. Il sistema impara dai tuoi documenti.",
    color: "bg-purple-50 text-purple-600",
  },
  {
    icon: Map,
    badge: "Zero clienti dimenticati",
    title: "Mappa del Territorio",
    description:
      "Vedi chi ha ordinato SS/FW e chi ha saltato la stagione. Pianifica il giro visite ottimizzato.",
    color: "bg-emerald-50 text-emerald-600",
  },
  {
    icon: Sparkles,
    badge: "Pipeline sempre piena",
    title: "AI Prospecting",
    description:
      "L'AI trova nuovi clienti simili ai tuoi migliori e suggerisce come approcciarli.",
    color: "bg-amber-50 text-amber-600",
  },
  {
    icon: LayoutGrid,
    badge: "Nessuna opportunità persa",
    title: "Gestione Portfolio Brand",
    description:
      "Marchi offerti, richiesti e concorrenti. Analisi gap per espandere il portfolio.",
    color: "bg-rose-50 text-rose-600",
  },
  {
    icon: TrendingUp,
    badge: "Decidi con i dati",
    title: "Performance & Analytics",
    description:
      "CLV, statistiche, andamento, analisi per mandante e zona. Tutto a portata di mano.",
    color: "bg-cyan-50 text-cyan-600",
  },
];

export function FeaturesGrid() {
  const scrollToFeature = (index: number) => {
    const element = document.getElementById(`feature-${index}`);
    element?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <section id="features" className="py-20 md:py-32 px-4 sm:px-6 lg:px-8 bg-gradient-to-b from-white to-honey-50">
      <div className="container mx-auto max-w-6xl">
        {/* Header */}
        <div className="text-center mb-16">
          <span className="inline-block text-xs font-bold uppercase tracking-widest text-ruby-600 mb-4">
            La soluzione
          </span>
          <h2 className="font-serif text-3xl md:text-4xl lg:text-5xl font-bold text-mocha-950 tracking-tight mb-4">
            6 pilastri per trasformare il tuo lavoro
          </h2>
          <p className="text-lg text-mocha-600 max-w-2xl mx-auto">
            KennyDB combina AI, automazione e intelligenza territoriale in un unico sistema pensato per gli agenti.
          </p>
        </div>

        {/* Features Grid */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {features.map((feature, index) => (
            <Card
              key={feature.title}
              className="group cursor-pointer border-mocha-100 hover:border-ruby-200 transition-all duration-300 hover:-translate-y-1 hover:shadow-xl bg-white"
              onClick={() => scrollToFeature(index)}
            >
              <CardContent className="p-6 md:p-8">
                {/* Icon & Badge */}
                <div className="flex items-start justify-between mb-5">
                  <div
                    className={`w-12 h-12 rounded-xl flex items-center justify-center ${feature.color}`}
                  >
                    <feature.icon className="w-6 h-6" />
                  </div>
                  <Badge
                    variant="secondary"
                    className="bg-ruby-50 text-ruby-700 hover:bg-ruby-100 text-[10px]"
                  >
                    {feature.badge}
                  </Badge>
                </div>

                {/* Content */}
                <h3 className="font-serif text-xl font-semibold text-mocha-950 mb-3 group-hover:text-ruby-600 transition-colors">
                  {feature.title}
                </h3>
                <p className="text-mocha-600 leading-relaxed text-sm">
                  {feature.description}
                </p>

                {/* CTA hint */}
                <div className="mt-5 flex items-center text-xs font-medium text-ruby-600 opacity-0 group-hover:opacity-100 transition-opacity">
                  Scopri di più
                  <svg
                    className="w-4 h-4 ml-1 transform group-hover:translate-x-1 transition-transform"
                    fill="none"
                    viewBox="0 0 24 24"
                    stroke="currentColor"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 8l4 4m0 0l-4 4m4-4H3"
                    />
                  </svg>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      </div>
    </section>
  );
}
